import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import * as Tone from 'tone';
import Wordmark from './Wordmark';
import { computePeaks, fillEnvelopeShape, sizeCanvas } from '../utils/waveform';
import { createLoopBuffer } from '../utils/loopBuffer';
import './DrumLoopEditor.css';

const WAVE_HEIGHT = 240;
const ACCENT = '#FF4141';
const IDLE_WAVE = '#DCDCDC';
const INK = '#111111';
const EDGE_GRAB_PX = 16;
const KNOB_RADIUS = 9;
const MAX_ZOOM = 200;

/**
 * DrumLoopEditor — separate page for picking which part of the backing drums
 * loops. Drag on the waveform to select a region; the endpoints are knob
 * handles you can grab and drag. Pinch (ctrl/cmd+wheel) zooms toward the
 * cursor (full-bleed while zoomed), two-finger scroll pans.
 */
const DrumLoopEditor = ({ audioUrl, fileName, loop, onSave, onBack, onReplace }) => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const playerRef = useRef(null);
  const playStartedAtRef = useRef(0);
  const dragRef = useRef(null); // { mode: 'start'|'end'|'new', anchor }
  const prevUrlRef = useRef(audioUrl);

  const zoomRef = useRef(1);
  const viewStartRef = useRef(0);
  const [isZoomed, setIsZoomed] = useState(false);

  const [buffer, setBuffer] = useState(null);
  const [sel, setSel] = useState(loop); // {start, end} seconds, null = none yet
  const [isPreviewing, setIsPreviewing] = useState(false);

  const selRef = useRef(sel);
  selRef.current = sel;

  // Load + decode the drums
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(audioUrl);
        const ab = await res.arrayBuffer();
        const buf = await Tone.context.decodeAudioData(ab);
        if (!cancelled) setBuffer(buf);
      } catch (e) {
        console.error('[DrumLoopEditor] failed to load drums:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [audioUrl]);

  const peaks = useMemo(() => (buffer ? computePeaks(buffer) : null), [buffer]);
  const duration = buffer?.duration || 0;

  // A replaced file arrives as a new audioUrl: clear the old selection,
  // zoom, and buffer while the new one decodes.
  useEffect(() => {
    if (prevUrlRef.current === audioUrl) return;
    prevUrlRef.current = audioUrl;
    setBuffer(null);
    setSel(null);
    zoomRef.current = 1;
    viewStartRef.current = 0;
    setIsZoomed(false);
  }, [audioUrl]);

  const stopPreview = useCallback(() => {
    if (playerRef.current) {
      try { playerRef.current.stop(); } catch { /* already stopped */ }
      playerRef.current.dispose();
      playerRef.current = null;
    }
    setIsPreviewing(false);
  }, []);

  const startPreview = useCallback(async () => {
    if (!buffer) return;
    await Tone.start();
    stopPreview();
    const s = selRef.current;
    // Preview exactly what the main player will loop: the selected segment
    // rendered with micro-fades at both ends so the wrap can't click.
    const loopBuffer = createLoopBuffer(buffer, s?.start, s?.end);
    const player = new Tone.Player(loopBuffer).toDestination();
    player.loop = true;
    player.start();
    playerRef.current = player;
    playStartedAtRef.current = Tone.now();
    setIsPreviewing(true);
  }, [buffer, stopPreview]);

  const togglePreview = useCallback(() => {
    if (isPreviewing) stopPreview();
    else startPreview();
  }, [isPreviewing, startPreview, stopPreview]);

  // Restart the preview when the selection is re-committed while previewing
  const commitSelection = useCallback(() => {
    if (isPreviewing) startPreview();
  }, [isPreviewing, startPreview]);

  useEffect(() => stopPreview, [stopPreview]); // stop on unmount

  // Space toggles preview (App's global handler no-ops on this page)
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === 'Space' && e.target.tagName !== 'INPUT') {
        e.preventDefault();
        togglePreview();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePreview]);

  const clampView = useCallback(() => {
    const maxStart = Math.max(0, duration - duration / zoomRef.current);
    viewStartRef.current = Math.max(0, Math.min(maxStart, viewStartRef.current));
  }, [duration]);

  // Pinch zoom / pan, same gesture language as the main waveform
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const oldZoom = zoomRef.current;
      const oldDur = duration / oldZoom;
      if (e.ctrlKey || e.metaKey) {
        const newZoom = Math.min(MAX_ZOOM, Math.max(1, oldZoom * Math.exp(-e.deltaY * 0.012)));
        const anchor = viewStartRef.current + frac * oldDur;
        zoomRef.current = newZoom;
        viewStartRef.current = anchor - frac * (duration / newZoom);
        setIsZoomed(newZoom > 1.001);
      } else if (oldZoom > 1) {
        viewStartRef.current += e.deltaX * (oldDur / rect.width);
      }
      clampView();
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [duration, clampView, buffer]);

  const xToTime = (clientX) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.max(0, Math.min(duration, viewStartRef.current + frac * (duration / zoomRef.current)));
  };

  const timeToClientX = (t) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const viewDur = duration / zoomRef.current;
    return rect.left + ((t - viewStartRef.current) / viewDur) * rect.width;
  };

  // Which edge (if any) is within grab range of this pointer position?
  const edgeAt = (clientX) => {
    const s = selRef.current;
    if (!s) return null;
    const dStart = Math.abs(clientX - timeToClientX(s.start));
    const dEnd = Math.abs(clientX - timeToClientX(s.end));
    if (dStart <= EDGE_GRAB_PX && dStart <= dEnd) return 'start';
    if (dEnd <= EDGE_GRAB_PX) return 'end';
    return null;
  };

  const handlePointerDown = (e) => {
    if (!duration) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const edge = edgeAt(e.clientX);
    if (edge) {
      dragRef.current = { mode: edge };
      return;
    }
    const t = xToTime(e.clientX);
    dragRef.current = { mode: 'new', anchor: t };
    setSel({ start: t, end: t });
  };

  const handlePointerMove = (e) => {
    if (!duration) return;
    const drag = dragRef.current;
    if (!drag) {
      // Hover feedback: resize cursor near a grabbable endpoint
      canvasRef.current.style.cursor = edgeAt(e.clientX) ? 'ew-resize' : 'crosshair';
      return;
    }
    const t = xToTime(e.clientX);
    const s = selRef.current;
    if (drag.mode === 'new') {
      setSel({ start: Math.min(drag.anchor, t), end: Math.max(drag.anchor, t) });
    } else if (drag.mode === 'start' && s) {
      setSel({ start: Math.min(t, s.end - 0.05), end: s.end });
    } else if (drag.mode === 'end' && s) {
      setSel({ start: s.start, end: Math.max(t, s.start + 0.05) });
    }
  };

  const handlePointerUp = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    const s = selRef.current;
    // Discard accidental zero-width selections
    if (s && s.end - s.start < 0.05) setSel(loop || null);
    else commitSelection();
  };

  // Draw loop: envelope + selection mask + knob handles + preview playhead,
  // all mapped through the zoom window.
  useEffect(() => {
    let raf;
    const tick = () => {
      const canvas = canvasRef.current;
      if (canvas && peaks && duration) {
        const cssW = canvas.clientWidth;
        const ctx = sizeCanvas(canvas, cssW, WAVE_HEIGHT);
        ctx.clearRect(0, 0, cssW, WAVE_HEIGHT);

        const s = selRef.current;
        const t0 = viewStartRef.current;
        const viewDur = duration / zoomRef.current;
        const t1 = t0 + viewDur;
        const px = cssW / viewDur;

        const regions = s
          ? [[0, s.start, IDLE_WAVE], [s.start, s.end, ACCENT], [s.end, duration, IDLE_WAVE]]
          : [[0, duration, IDLE_WAVE]];
        for (const [a, b, color] of regions) {
          const ra = Math.max(a, t0);
          const rb = Math.min(b, t1);
          if (rb <= ra) continue;
          fillEnvelopeShape(ctx, peaks, duration, ra, rb, (ra - t0) * px, (rb - t0) * px, WAVE_HEIGHT, color);
        }

        // Endpoint handles: full-height line + grabbable knob
        if (s) {
          for (const t of [s.start, s.end]) {
            if (t < t0 || t > t1) continue;
            const x = (t - t0) * px;
            ctx.fillStyle = INK;
            ctx.fillRect(x - 1, 0, 2, WAVE_HEIGHT);
            ctx.beginPath();
            ctx.arc(x, WAVE_HEIGHT / 2, KNOB_RADIUS, 0, Math.PI * 2);
            ctx.fillStyle = '#FFFFFF';
            ctx.fill();
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = INK;
            ctx.stroke();
          }
        }

        // Preview playhead
        if (isPreviewing && playerRef.current) {
          const start = s?.start || 0;
          const end = s?.end || duration;
          const loopLen = Math.max(0.05, end - start);
          const elapsed = Tone.now() - playStartedAtRef.current;
          const pos = start + (elapsed % loopLen);
          if (pos >= t0 && pos <= t1) {
            ctx.fillStyle = INK;
            ctx.fillRect((pos - t0) * px, 0, 1.5, WAVE_HEIGHT);
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [peaks, duration, isPreviewing]);

  const fmt = (t) => `${Math.floor(t / 60)}:${(t - Math.floor(t / 60) * 60).toFixed(2).padStart(5, '0')}`;

  return (
    <div className="loop-editor">
      <div className="loop-editor-brand"><Wordmark /></div>

      <h2 className="loop-editor-title">drum loop</h2>
      <div className="loop-editor-sub">
        drag to choose the part that loops · grab the round handles to adjust · pinch to zoom
      </div>
      {fileName && <div className="loop-editor-file">{fileName}</div>}

      <div className={`loop-editor-wave-wrap${isZoomed ? ' zoomed' : ''}`}>
        {buffer ? (
          <canvas
            ref={canvasRef}
            className="loop-editor-canvas"
            style={{ height: WAVE_HEIGHT }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
        ) : (
          <div className="loop-editor-loading">loading drums…</div>
        )}
      </div>

      <div className="loop-editor-readout">
        {sel
          ? `loop: ${fmt(sel.start)} → ${fmt(sel.end)} (${(sel.end - sel.start).toFixed(2)}s)`
          : 'no loop points set · the whole file loops'}
      </div>

      <div className="loop-editor-actions">
        <button type="button" className="loop-editor-btn" onClick={togglePreview}>
          {isPreviewing ? '■ stop' : '▶ preview'}
        </button>
        <button
          type="button"
          className="loop-editor-btn primary"
          onClick={() => { stopPreview(); onSave(sel); }}
        >
          save loop
        </button>
        {onReplace && (
          <button
            type="button"
            className="loop-editor-btn"
            onClick={() => { stopPreview(); fileInputRef.current?.click(); }}
            title="swap in a different drums audio file"
          >
            replace file
          </button>
        )}
        <button
          type="button"
          className="loop-editor-btn"
          onClick={() => { stopPreview(); onBack(); }}
        >
          back
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onReplace(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default DrumLoopEditor;
